/**
 * Settings page - tabbed layout for all user and admin settings
 */
import { useState } from 'react'
import { ProfileTab, AccountsTab, TagsTab, NotificationsTab, ApiKeysTab } from '../components/settings'
import { AdminInvitesPage } from './AdminInvites'
import { AdminTenantsPage } from './AdminTenants'
import { AdminUsersPage } from './AdminUsers'

// Tab types - grouped by category
type SettingsTab = 'profile' | 'accounts' | 'tags' | 'apikeys' | 'notifications' | 'tenants' | 'users' | 'invites'

interface TabDef {
  id: SettingsTab
  label: string
  category: 'user' | 'admin'
}

// Tab definitions with categories
const tabs: TabDef[] = [
  { id: 'profile', label: 'Profile', category: 'user' },
  { id: 'accounts', label: 'Accounts', category: 'user' },
  { id: 'tags', label: 'Tags', category: 'user' },
  { id: 'apikeys', label: 'API Keys', category: 'user' },
  { id: 'notifications', label: 'Notifications', category: 'user' },
  { id: 'tenants', label: 'Tenants', category: 'admin' },
  { id: 'users', label: 'Users', category: 'admin' },
  { id: 'invites', label: 'Invite Codes', category: 'admin' },
]

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  const userTabs = tabs.filter(t => t.category === 'user')
  const adminTabs = tabs.filter(t => t.category === 'admin')

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileTab />
      case 'accounts':
        return <AccountsTab />
      case 'tags':
        return <TagsTab />
      case 'apikeys':
        return <ApiKeysTab />
      case 'notifications':
        return <NotificationsTab />
      case 'tenants':
        return <AdminTenantsPage embedded />
      case 'users':
        return <AdminUsersPage />
      case 'invites':
        return <AdminInvitesPage embedded />
      default:
        return null
    }
  }

  const renderTabButton = (tab: TabDef) => (
    <button
      key={tab.id}
      onClick={() => setActiveTab(tab.id)}
      className={`
        px-4 py-2 text-sm font-medium rounded-md transition-colors
        ${activeTab === tab.id
          ? 'bg-primary-100 text-primary-700 border border-primary-200'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }
      `}
    >
      {tab.label}
    </button>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account, preferences, and admin settings
        </p>
      </div>

      {/* Tab Navigation - Card style */}
      <div className="bg-white rounded-lg shadow p-2">
        <div className="flex flex-wrap gap-2">
          {/* User Settings */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider px-2">User</span>
            {userTabs.map(renderTabButton)}
          </div>

          {/* Divider */}
          <div className="w-px bg-gray-200 mx-2 self-stretch" />

          {/* Admin Settings */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider px-2">Admin</span>
            {adminTabs.map(renderTabButton)}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {renderTabContent()}
      </div>
    </div>
  )
}
